import axios from 'axios';
import * as Pixel from './MetaPixelEvents';
import { ph_capture } from './posthog';

const ANON_ID_KEY = 'tl_anon_id';
const SESSION_ID_KEY = 'tl_session_id';

const make_uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const get_anon_id = () => {
  try {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id = make_uuid();
      localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return make_uuid();
  }
};

const get_session_id = () => {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = make_uuid();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return make_uuid();
  }
};

const default_context = () => ({
  app_version: process.env.REACT_APP_VERSION || 'dev',
  platform: 'web',
  locale: typeof navigator !== 'undefined' ? navigator.language : null,
  country: null,
  session_id: get_session_id()
});

const PIXEL_MAP = {
  signup_completed: () => Pixel.trackSignUp && Pixel.trackSignUp(0),
  email_verified: () => Pixel.trackCompleteRegistration && Pixel.trackCompleteRegistration('successful'),
  checkout_completed: (props) => Pixel.trackPurchase && Pixel.trackPurchase(props?.mrr || 0, props?.currency || 'USD'),
  pricing_viewed: () => Pixel.trackContact && Pixel.trackContact()
};

let queue = [];
let flushing = false;

const endpoint = () => ((typeof global !== 'undefined' && global.SERVIP) || '') + '/events/track';

const flush = () => {
  if (flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue.splice(0, queue.length);
  Promise.all(batch.map((payload) =>
    axios.post(endpoint(), payload).catch(() => {})
  )).finally(() => {
    flushing = false;
    if (queue.length > 0) schedule_flush();
  });
};

const schedule_flush = () => {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(flush, { timeout: 2000 });
  } else {
    setTimeout(flush, 500);
  }
};

export const track = (event_name, properties = {}) => {
  if (!event_name) return;

  try {
    const fn = PIXEL_MAP[event_name];
    if (fn) fn(properties);
  } catch (e) {}

  // Mirror to PostHog. Same event name + props go to /events/track AND
  // posthog.capture, so funnels can be built in either backend without
  // double-instrumenting every call site.
  try { ph_capture(event_name, properties); } catch (e) {}

  const payload = {
    event_id: make_uuid(),
    event_name,
    properties: properties || {},
    context: default_context(),
    anonymous_id: get_anon_id(),
    session_id: get_session_id()
  };

  queue.push(payload);
  schedule_flush();
};

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (queue.length === 0) return;
    const url = endpoint();
    const batch = queue.splice(0, queue.length);
    for (const payload of batch) {
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
        }
      } catch (e) {}
    }
  });
}

export default track;
